import { EventType } from "@/analytics/analytics-events";
import { useUpdateProjectMetadata } from "@/components/common/project-provider/use-project-metadata";
import { AccountSettingsDialog } from "@/components/ui/account-settings-dialog";
import { ToolBreadcrumbs } from "@/components/ui/tool-breadcrumbs";
import { useAlignmentOverlay } from "@/registration-tools/common/alignment-overlay-context";
import { RegistrationContextProvider } from "@/registration-tools/common/registration-context";
import { selectPointCloudTransform } from "@/registration-tools/common/store/registration-selectors";
import { runtimeConfig } from "@/runtime-config";
import { selectAreaFor } from "@/store/selections-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  redirectToDashboardProjectPage,
  redirectToViewer,
} from "@/utils/redirects";
import {
  FaroDialog,
  HeaderBar,
  selectChildDepthFirst,
  selectDashboardUrl,
  useLocalStorage,
} from "@faro-lotv/app-component-toolbox";
import { HelpBanner } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import {
  IElementSectionDataSession,
  isIElementGenericDataset,
} from "@faro-lotv/ielement-types";
import {
  RegisteredUserInfo,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Box, Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { selectMainPointCloudFromDataSession } from "../common/select-main-point-clouds";
import { applyPairwiseRegistration } from "../utils/apply-pairwise-registration";
import { PairwiseRegistrationBar } from "./pairwise-registration-bar";
import { PairwiseRegistrationCanvas } from "./pairwise-registration-canvas";
import { PairwiseRegistrationQuickHelp } from "./pairwise-registration-quick-help";

export type PairwiseRegistrationUiProps = {
  /** The Section.DataSession element which serves as reference (already aligned). */
  refCloudDataSession: IElementSectionDataSession;

  /** The Section.DataSession element which needs to be adjusted. */
  modelCloudDataSession: IElementSectionDataSession;

  /**
   * Optional, Enables redirect to the dashboard after the workflow finishes if not null.
   * If not null, the string is used to call redirectToDashboardProjectPage which allows
   * to define the target tab of the project details in the dashboard.
   * If null or not assigned the viewer will be opened after the alignment is saved.
   */
  dashboardRedirect?: string;

  /** The currently logged in user. */
  currentUser: RegisteredUserInfo;
};

/**
 * @returns UI with Canvas and Utility Bars
 */
export function PairwiseRegistrationUi({
  refCloudDataSession,
  modelCloudDataSession,
  dashboardRedirect,
  currentUser,
}: PairwiseRegistrationUiProps): JSX.Element | null {
  const store = useAppStore();
  const { requestLogin, logout } = useAuthContext();

  const [quickHelpFirstVisit, setQuickHelpFirstVisit] = useLocalStorage(
    "isPairwiseFirstVisit",
    true,
  );

  const [isQuickHelpOpen, setIsQuickHelpOpen] = useState(quickHelpFirstVisit);
  const [isSettingPageOpen, setIsSettingPageOpen] = useState<boolean>(false);

  const [isHelpBannerOpen, setIsHelpBannerOpen] = useState(true);

  const { initialModelTransform } = useAlignmentOverlay();

  const { projectApiClient: projectApi } = useApiClientContext();

  // Non-blocking: Load project meta-data, e.g. to get the project name for the breadcrumbs
  useUpdateProjectMetadata(projectApi.projectId);

  const area = useAppSelector(selectAreaFor(refCloudDataSession));

  const refCloudDataSetElement = useAppSelector(
    selectChildDepthFirst(refCloudDataSession, isIElementGenericDataset, 1),
  );
  assert(
    refCloudDataSetElement,
    `reference data session ${refCloudDataSession.id} does not have a dataset as a child`,
  );
  const modelCloudDataSetElement = useAppSelector(
    selectChildDepthFirst(modelCloudDataSession, isIElementGenericDataset, 1),
  );
  assert(
    modelCloudDataSetElement,
    `model data session ${modelCloudDataSession.id} does not have a dataset as a child`,
  );

  const pointCloudNames = useMemo(
    () => [refCloudDataSession.name, modelCloudDataSession.name],
    [refCloudDataSession.name, modelCloudDataSession.name],
  );

  const pointCloudLabels = ["Reference", "Adjustable"];

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const pcTransformArray = useAppSelector(selectPointCloudTransform);
  const modelPointCloudStream = useAppSelector(
    selectMainPointCloudFromDataSession(modelCloudDataSession),
  );
  assert(modelPointCloudStream, "The model point cloud stream must exist");

  const activeRefPointCloud = useAppSelector(
    selectMainPointCloudFromDataSession(refCloudDataSession),
  );
  assert(activeRefPointCloud, "The reference point cloud stream must exist");

  const redirectToDashboardOrViewer = useCallback(() => {
    if (dashboardRedirect === undefined) {
      redirectToViewer(projectApi.projectId);
    } else {
      redirectToDashboardProjectPage(
        projectApi.projectId,
        dashboardRedirect,
        selectDashboardUrl(store.getState()),
      );
    }
  }, [dashboardRedirect, projectApi, store]);

  return (
    <>
      <FaroDialog
        open={isCancelDialogOpen}
        title="Leave the pairwise registration?"
        onConfirm={redirectToDashboardOrViewer}
        onCancel={() => setIsCancelDialogOpen(false)}
      >
        Any changes made will be lost.
      </FaroDialog>
      <RegistrationContextProvider>
        <Stack
          sx={{
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <HeaderBar
            showProfileButton
            content={
              <ToolBreadcrumbs
                toolName="Pairwise Registration"
                onRedirectToViewerClicked={() => {
                  Analytics.track(EventType.abortPairwiseRegistrationWorkflow);
                  if (isEqual(initialModelTransform, pcTransformArray)) {
                    redirectToDashboardOrViewer();
                  } else {
                    setIsCancelDialogOpen(true);
                  }
                }}
              />
            }
            userDisplayInfo={currentUser}
            links={runtimeConfig.externalLinks}
            onLogInClick={requestLogin}
            isQuickHelpOpen={isQuickHelpOpen}
            onQuickHelpClick={() => {
              setQuickHelpFirstVisit(false);
              setIsQuickHelpOpen(!isQuickHelpOpen);
            }}
            onLogOutClick={logout}
            onAccountAndSecurityClick={() => setIsSettingPageOpen(true)}
          />
          {isSettingPageOpen && (
            <AccountSettingsDialog
              onClose={() => setIsSettingPageOpen(false)}
            />
          )}
          <PairwiseRegistrationBar
            areaName={area?.name}
            pointCloudNames={pointCloudNames}
            pointCloudLabels={pointCloudLabels}
            onConfirmClick={() => {
              // Alignment was saved, redirect user back
              if (!pcTransformArray) {
                throw Error("No transform found for model cloud.");
              }
              applyPairwiseRegistration(
                projectApi,
                modelPointCloudStream,
                pcTransformArray,
              ).then(() => {
                Analytics.track(EventType.finishPairwiseRegistrationWorkflow);
                redirectToViewer(projectApi.projectId);
              });
            }}
          />
          <Stack
            sx={{
              height: "inherit",
              width: "100%",
              overflow: "hidden",
              position: "relative",
            }}
            direction="row"
          >
            {isHelpBannerOpen && (
              <Box
                component="div"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "absolute",
                  zIndex: 1,
                  width: "100%",
                }}
              >
                <HelpBanner
                  sx={{
                    display: "inline-block",
                    margin: 1,
                  }}
                >
                  Do a visual registration first, then press “Save & Exit” to
                  confirm.
                </HelpBanner>
              </Box>
            )}

            <PairwiseRegistrationCanvas
              activeModelPointCloud={modelPointCloudStream}
              activeRefPointCloud={activeRefPointCloud}
              onVisualRegistration={() => setIsHelpBannerOpen(false)}
            />
            {/* Drawer does not resize canvas*/}
            {isQuickHelpOpen && (
              <PairwiseRegistrationQuickHelp
                onClose={() => {
                  setQuickHelpFirstVisit(false);
                  setIsQuickHelpOpen(false);
                }}
              />
            )}
          </Stack>
        </Stack>
      </RegistrationContextProvider>
    </>
  );
}
